import moment from "moment";

/**
 * Gets the team member names working on a task's current execution
 * @param {Object} task - The task object
 * @returns {string} Comma-separated team member names
 */
export function getTeamMemberNames(task) {
  if (!task.execution || task.execution.length === 0) {
    return '';
  }
  const lastExecution = task.execution[task.execution.length - 1];
  if (!lastExecution.team_members || lastExecution.team_members.length === 0) {
    return '';
  }
  return lastExecution.team_members.map(tm => tm.name).join(', ');
}

/**
 * Gets the start time of the current task execution
 * @param {Object} task - The task object
 * @returns {string|null} Formatted start time or null
 */
export function getTaskStartTime(task) {
  if (!task.execution || task.execution.length === 0) {
    return null;
  }
  const lastExecution = task.execution[task.execution.length - 1];
  if (!lastExecution.start) {
    return null;
  }

  return moment(lastExecution.start).format('hh:mm a');
}

/**
 * Gets the formatted elapsed time for a task
 * @param {Object} task - The task object
 * @returns {string|null} Formatted elapsed time or null
 */
export function getTaskElapsedTime(task) {
  const totalMinutes = getTaskElapsedMinutes(task);

  if (totalMinutes === 0) {
    return null;
  }

  // Convert to hours and minutes
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
}

/**
 * Gets the total elapsed minutes for a task across all executions
 * @param {Object} task - The task object
 * @returns {number} Total elapsed minutes
 */
export function getTaskElapsedMinutes(task) {
  if (!task.execution || task.execution.length === 0) {
    return 0;
  }

  let totalElapsedMinutes = 0;

  // Sum up all previous completed executions
  for (let i = 0; i < task.execution.length; i++) {
    const execution = task.execution[i];

    if (execution.start) {
      if (execution.stop) {
        // Completed execution - add the duration
        const startTime = new Date(execution.start);
        const stopTime = new Date(execution.stop);
        const executionMs = stopTime - startTime;
        totalElapsedMinutes += Math.floor(executionMs / (1000 * 60));
      } else {
        // Currently running execution - add elapsed time since start
        const startTime = new Date(execution.start);
        const currentTime = new Date();
        const currentElapsedMs = currentTime - startTime;
        totalElapsedMinutes += Math.floor(currentElapsedMs / (1000 * 60));
      }
    }
  }

  return totalElapsedMinutes;
}

/**
 * Gets the formatted estimate time for a task
 * @param {Object} task - The task object
 * @returns {string|null} Formatted estimate time or null
 */
export function getTaskEstimate(task) {
  // Check if task has an estimated duration (timeEstimate is typically in minutes)
  let timeEstimate = task.source.timeEstimate;
  if (timeEstimate && timeEstimate > 0) {
    const hours = Math.round(Math.floor(timeEstimate / 60));
    const minutes = Math.round(timeEstimate % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }
  return null;
}

/**
 * Gets the estimate time in minutes for a task
 * @param {Object} task - The task object
 * @returns {number} Estimate time in minutes
 */
export function getTaskEstimateMinutes(task) {
  return task.source.timeEstimate || 0;
}

/**
 * Checks if a task is over its estimate
 * @param {Object} task - The task object
 * @returns {boolean} True if over estimate
 */
export function isTaskOverEstimate(task) {
  const estimateMinutes = getTaskEstimateMinutes(task);
  if (estimateMinutes <= 0) return false;

  const elapsedMinutes = getTaskElapsedMinutes(task);
  return elapsedMinutes > estimateMinutes;
}

/**
 * Checks if a task is critically over its estimate (2x or more)
 * @param {Object} task - The task object
 * @returns {boolean} True if critically over estimate
 */
export function isTaskCriticallyOver(task) {
  const estimateMinutes = getTaskEstimateMinutes(task);
  if (estimateMinutes <= 0) return false;

  const elapsedMinutes = getTaskElapsedMinutes(task);
  return elapsedMinutes >= (estimateMinutes * 2);
}

/**
 * Gets the duration warning color for a task
 * @param {Object} task - The task object
 * @returns {string} Color name for warnings
 */
export function getDurationWarningColor(task) {
  if (isTaskCriticallyOver(task)) {
    return 'error'; // Red for critically over (2x or more)
  } else if (isTaskOverEstimate(task)) {
    return 'warning'; // Orange for over estimate
  }
  return 'grey'; // Default grey for normal duration
}

/**
 * Gets the duration warning icon for a task
 * @param {Object} task - The task object
 * @returns {string} Icon name for warnings
 */
export function getDurationWarningIcon(task) {
  if (isTaskCriticallyOver(task)) {
    return 'mdi-timer-alert'; // Different icon for critical
  } else if (isTaskOverEstimate(task)) {
    return 'mdi-timer-alert-outline'; // Warning timer icon
  }
  return 'mdi-timer'; // Default timer icon
}

/**
 * Gets the duration warning CSS class for a task
 * @param {Object} task - The task object
 * @returns {string} CSS class names for warnings
 */
export function getDurationWarningClass(task) {
  if (isTaskCriticallyOver(task)) {
    return 'error--text font-weight-bold'; // Bold red text
  } else if (isTaskOverEstimate(task)) {
    return 'warning--text font-weight-medium'; // Medium orange text
  }
  return ''; // Default styling
}

/**
 * Gets the progress percentage for a task
 * @param {Object} task - The task object
 * @returns {number} Progress percentage (0-100)
 */
export function getTaskProgressPercentage(task) {
  const estimateMinutes = getTaskEstimateMinutes(task);
  const elapsedMinutes = getTaskElapsedMinutes(task);

  if (estimateMinutes <= 0) return 0;

  // Calculate percentage, but cap at 100% for display purposes
  // The progress bar will show full when over time, but the color will indicate the issue
  const percentage = (elapsedMinutes / estimateMinutes) * 100;
  return Math.min(percentage, 100);
}

/**
 * Gets the progress bar color for a task
 * @param {Object} task - The task object
 * @returns {string} Color name for progress bar
 */
export function getProgressBarColor(task) {
  if (isTaskCriticallyOver(task)) {
    return 'error'; // Red progress bar for critically over
  } else if (isTaskOverEstimate(task)) {
    return 'warning'; // Orange progress bar for over estimate
  } else {
    const percentage = getTaskProgressPercentage(task);
    if (percentage >= 80) {
      return 'warning'; // Yellow/orange when approaching completion
    } else if (percentage >= 60) {
      return 'info'; // Blue when making good progress
    } else {
      return 'success'; // Green for early progress
    }
  }
}

/**
 * Gets the progress text for a task
 * @param {Object} task - The task object
 * @returns {string} Progress text description
 */
export function getProgressText(task) {
  const estimateMinutes = getTaskEstimateMinutes(task);
  const elapsedMinutes = getTaskElapsedMinutes(task);

  if (estimateMinutes <= 0) return '';

  const percentage = Math.round((elapsedMinutes / estimateMinutes) * 100);

  if (elapsedMinutes >= estimateMinutes) {
    const overMinutes = elapsedMinutes - estimateMinutes;
    const overHours = Math.round(Math.floor(overMinutes / 60));
    const remainingMinutes = Math.round(overMinutes % 60);

    let overText = '';
    if (overHours > 0) {
      overText = `${overHours}h ${remainingMinutes}m`;
    } else {
      overText = `${remainingMinutes}m`;
    }

    return `Over time by ${overText} (${percentage}%)`;
  } else {
    return `${percentage}% complete`;
  }
}

/**
 * Gets the progress text CSS class for a task
 * @param {Object} task - The task object
 * @returns {string} CSS class names for progress text
 */
export function getProgressTextClass(task) {
  if (isTaskCriticallyOver(task)) {
    return 'error--text font-weight-bold';
  } else if (isTaskOverEstimate(task)) {
    return 'warning--text font-weight-medium';
  }
  return 'grey--text';
}